import {
  IAppointment,
  IAppointmentDependency,
  IBrand,
  ICalendarEvent,
  IContact,
  IDataChange,
  IEvent,
  IFollowUp,
  IInteractionV2,
  IInvoice,
  ILab,
  INote,
  IParticipant,
  IPatient,
  IPatientRelationship,
  IPaymentPlan,
  IPrincipleMention,
  IScheduleSummary,
  ISchedulingEvent,
  ISchedulingEventReason,
  IStaffer,
  ITeam,
  ITransaction,
  ITreatmentCategory,
  ITreatmentPlan,
  ITreatmentStep,
  IWaitListItem,
  InvoiceStatus,
  LabJobStatus,
  PaymentPlanStatus,
  TaskStatus,
  TransactionStatus,
  TreatmentPlanStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Reffable,
  WithRef,
} from '@principle-theorem/shared';
import {
  IBaseAccountCreditTopicData,
  IBaseAppointmentTopicData,
  IBaseCalendarEventTopicData,
  IBaseLabJobTopicData,
  IBasePatientTopicData,
  IBasePaymentPlanTopicData,
  IBaseRecurringTaskTopicData,
  IBaseTaskTopicData,
  IBrandPubSubData,
  IOrganisationPubSubData,
  IPracticePubSubData,
} from './base-topic-data';

export interface ILabWriteTopicData extends IBrandPubSubData {
  lab: WithRef<ILab>;
}

export interface IBaseTreatmentPlanTopicData extends IBasePatientTopicData {
  treatmentPlan: WithRef<ITreatmentPlan>;
}

export type ITreatmentPlanWriteTopicData = IBaseTreatmentPlanTopicData;

export interface IBaseTreatmentStepTopicData extends IBasePatientTopicData {
  treatmentStep: WithRef<ITreatmentStep>;
}

export type ITreatmentStepWriteTopicData = IBaseTreatmentStepTopicData;

export type IAccountCreditWriteTopicData = IBaseAccountCreditTopicData;

export type IAppointmentWriteTopicData = IBaseAppointmentTopicData;

export type IBrandWriteTopicData = IBrandPubSubData;

export interface IContactWriteTopicData extends IBrandPubSubData {
  contact: WithRef<IContact>;
}

export interface IInvoiceWriteTopicData extends IPracticePubSubData {
  patient: WithRef<IPatient>;
  invoice: WithRef<IInvoice>;
  transactions: WithRef<ITransaction>[];
  status: IDataChange<InvoiceStatus | undefined>;
}

export interface ITransactionWriteTopicData extends IPracticePubSubData {
  patient: WithRef<IPatient>;
  invoice: WithRef<IInvoice>;
  transaction: Reffable<ITransaction>;
  status: IDataChange<TransactionStatus | undefined>;
}

export type ILabJobWriteTopicData = IBaseLabJobTopicData;

export interface ILabJobStatusUpdateTopicData extends IBaseLabJobTopicData {
  status: IDataChange<LabJobStatus>;
}

export type ILabJobReceivedTopicData = IBaseLabJobTopicData;

export type IPatientWriteTopicData = IBasePatientTopicData;

export interface IPatientInteractionWriteTopicData extends IBrandPubSubData {
  patientRef: DocumentReference<IPatient>;
  interaction: WithRef<IInteractionV2>;
}

export type IPaymentPlanWriteTopicData = IBasePaymentPlanTopicData;

export interface IPaymentPlanStatusUpdateTopicData extends IBrandPubSubData {
  patient: WithRef<IPatient>;
  paymentPlan: WithRef<IPaymentPlan>;
  status: IDataChange<PaymentPlanStatus>;
}

export type IPracticeWriteTopicData = IPracticePubSubData;

export interface IStafferWriteTopicData extends IBrandPubSubData {
  staffer: WithRef<IStaffer>;
}

export type ITaskWriteTopicData = IBaseTaskTopicData;

export type ITaskCompletedTopicData = IBaseTaskTopicData;

export interface ITaskStatusUpdateTopicData extends IBaseTaskTopicData {
  status: IDataChange<TaskStatus>;
}

export interface ITeamWriteTopicData extends IBrandPubSubData {
  team: WithRef<ITeam>;
}

export type ITreatmentPlanCompletedTopicData = IBaseTreatmentPlanTopicData;

export interface ITreatmentPlanStatusUpdateTopicData
  extends IBaseTreatmentPlanTopicData {
  status: IDataChange<TreatmentPlanStatus | undefined>;
}

export interface IScheduleSummaryWriteTopicData extends IPracticePubSubData {
  scheduleSummary: WithRef<IScheduleSummary>;
}

export interface ISchedulingEventWriteTopicData
  extends IBaseAppointmentTopicData {
  schedulingEvent: WithRef<ISchedulingEvent>;
}

export interface ISchedulingEventReasonWriteTopicData extends IBrandPubSubData {
  schedulingEventReason: WithRef<ISchedulingEventReason>;
}

export interface IAppointmentRescheduleTopicData extends IPracticePubSubData {
  patient: WithRef<IPatient>;
  appointment: IDataChange<WithRef<IAppointment>>;
  treatmentCategory?: WithRef<ITreatmentCategory>;
  event: IDataChange<IEvent | undefined>;
}

export interface IAppointmentCancelTopicData extends IPracticePubSubData {
  patient: WithRef<IPatient>;
  appointment: IDataChange<WithRef<IAppointment>>;
  event: IDataChange<IEvent | undefined>;
}

export type IAppointmentAddTopicData = IBaseAppointmentTopicData;

export type ITaskAddTopicData = IBaseTaskTopicData;

export type IRecurringTaskAddTopicData = IBaseRecurringTaskTopicData;

export type IBrandAddTopicData = IBrandPubSubData;

export type ITaskReopenedTopicData = IBaseTaskTopicData;

export type ITaskRemoveTopicData = IBaseTaskTopicData;

export type ITaskReassignTopicData = IBaseTaskTopicData;

export interface IAddedPaymentPlanInvoicesData
  extends IBasePaymentPlanTopicData {
  invoiceRefs: DocumentReference<IInvoice>[];
}

export interface IPaymentPlanInvoiceAddTopicData
  extends IBasePaymentPlanTopicData {
  invoice: WithRef<IInvoice>;
}

export type IPaymentPlanCompleteTopicData = IBasePaymentPlanTopicData;

export interface IAddedPatientRelationshipData extends IBasePatientTopicData {
  relationships: IPatientRelationship[];
}

export interface IPatientRelationshipAddTopicData
  extends IBasePatientTopicData {
  relationship: IPatientRelationship;
}

export interface IAddedPatientNoteData extends IBasePatientTopicData {
  notes: INote[];
}

export interface IPatientNoteAddTopicData extends IBasePatientTopicData {
  note: INote;
}

export type IOrganisationAddTopicData = IOrganisationPubSubData;

export interface IAddedMentionData {
  mentions: IPrincipleMention[];
}

export interface IMentionAddTopicData {
  mention: IPrincipleMention;
}

export interface IAddedNoteData {
  notes: INote[];
}

export interface INoteAddTopicData {
  note: INote;
}

export interface ICalendarEventRescheduleTopicData {
  brand: WithRef<IBrand>;
  staffer: WithRef<IStaffer>;
  calendarEvent: IDataChange<WithRef<ICalendarEvent>>;
  event: IDataChange<IEvent>;
}

export interface IRemovedCalendarEventParticipantsData
  extends IBaseCalendarEventTopicData {
  participants: IParticipant[];
}

export interface ICalendarEventParticipantRemoveTopicData
  extends IBaseCalendarEventTopicData {
  participant: IParticipant;
}

export interface IAddedCalendarEventParticipantsData
  extends IBaseCalendarEventTopicData {
  participants: IParticipant[];
}

export interface ICalendarEventParticipantAddTopicData
  extends IBaseCalendarEventTopicData {
  participant: IParticipant;
}

export type ICalendarEventAddTopicData = IBaseCalendarEventTopicData;

export interface IWaitListUpdateTopicData extends IBaseAppointmentTopicData {
  waitListItem: IDataChange<IWaitListItem | undefined>;
}

export interface IAppointmentFollowUpAddTopicData
  extends IBaseAppointmentTopicData {
  followUp: IFollowUp;
}

export interface IAddedAppointmentDependenciesData
  extends IBaseAppointmentTopicData {
  dependencies: IAppointmentDependency[];
}

export interface IAppointmentDependencyAddTopicData
  extends IBaseAppointmentTopicData {
  dependency: IAppointmentDependency;
}

export type IAppointmentCompletedTopicData = IBaseAppointmentTopicData;
